import { isDevOrCiEnvironment } from "~/lib/environment";
import {
  claimTrackingFlag,
  hasMarketingConsent,
  trackingFlagClaimed,
} from "~/lib/tracking";

const MASSLYTICS_BRAND_ID = "SW-752344";
const MASSLYTICS_ENDPOINT = "https://app.masslytics.io/api/masslytics";
// Masslytics matches conversions on this URL; it is a fixed identifier, not the page we're on.
const MASSLYTICS_CONVERSION_URL = "https://app.lekkerladen.com/conversie";

// The pixel keeps Session-ID in sessionStorage, which is per origin: lekkerladen.com
// passes its id along in this query param so the conversion can be tied to that visit.
const MASSLYTICS_SESSION_PARAM = "mlsid";

export function captureMasslyticsSession() {
  if (typeof window === "undefined") return;
  const sessionId = new URLSearchParams(window.location.search).get(
    MASSLYTICS_SESSION_PARAM,
  );
  if (!sessionId) return;
  // No consent gate here: this only moves an id from the URL into sessionStorage
  // and fires nothing. The conversion call below is what's gated, so an id stored
  // without consent stays inert.
  try {
    window.sessionStorage.setItem("Session-ID", sessionId);
  } catch {
    // sessionStorage can throw in private mode or when storage is blocked.
  }
}

/**
 * Report a Masslytics signup conversion for a newly registered user.
 *
 * Consistent with the Awin lead conversion: skipped on dev/ci, gated on
 * Cookiebot marketing consent, and de-duped once per user id so an OtpStep
 * re-render or a repeated submit cannot double-count. Callers must only invoke
 * this for genuinely new registrations, never for an existing user signing in.
 */
export function trackMasslyticsConversion(userId: string | undefined | null) {
  if (typeof window === "undefined" || !userId) return;
  // Local dev and CI signups would pollute the Masslytics dashboard with fake conversions.
  if (isDevOrCiEnvironment()) return;

  const flag = `masslytics_signup_fired:${userId}`;
  if (trackingFlagClaimed(flag)) return;
  // Masslytics sets a marketing cookie; leave the flag unclaimed until consent is
  // in so a later consented visit still reports the conversion.
  if (!hasMarketingConsent()) return;
  claimTrackingFlag(flag);

  let sessionId = "unknown";
  try {
    sessionId = window.sessionStorage.getItem("Session-ID") ?? "unknown";
  } catch {
    // sessionStorage can throw in private mode or when storage is blocked.
  }

  void fetch(MASSLYTICS_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      "Brand-ID": MASSLYTICS_BRAND_ID,
      "Session-ID": sessionId,
      "Current-URL": MASSLYTICS_CONVERSION_URL,
      Referer: document.referrer || "",
    }),
    keepalive: true,
  }).catch((error: unknown) => {
    console.error("Masslytics conversion call failed:", error);
  });
}
